import { Pagination } from '@/constants/pagination';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SettingFilterState = {
  includePendingInvites?: boolean;
  fromDate?: string;
  toDate?: string;
  subCustomerUserId?: string;
};

export type SettingState = {
  data: SettingType[];
  dataCompanySecurity: SettingCompanySecurity[];
  pagination: PaginationAPI;
  totalElements?: number;
  loading: boolean;
  filters: SettingFilterState;
  dataDetail: SettingDetailType;
  dataAssignVA: AssignVirtualAccountSetting[];
  dataAssignVC: AssignVirtualCardsSetting[];
  dataAssignGC: AssignGroupCardsSetting[];
  telegramBots: TelegramBot[];
  telegramBotsPagination: PaginationAPI;
  transactionNotificationRules: TransactionNotificationRule[];
  transactionNotificationRulesPagination: PaginationAPI;
  balanceAlertRules: BalanceAlertRule[];
  balanceAlertRulesPagination: PaginationAPI;
};

export const settingInitialState: SettingState = {
  data: [],
  dataCompanySecurity: [],
  dataAssignVA: [],
  dataAssignVC: [],
  dataAssignGC: [],
  pagination: {
    Page: Pagination.Page,
    totalPages: 0,
    totalElements: 0,
    Size: Pagination.Size,
  },
  loading: false,
  filters: {},
  dataDetail: {},
  telegramBots: [],
  telegramBotsPagination: {
    Page: Pagination.Page,
    totalPages: 0,
    totalElements: 0,
    Size: Pagination.Size,
  },
  transactionNotificationRules: [],
  transactionNotificationRulesPagination: {
    Page: Pagination.Page,
    totalPages: 0,
    totalElements: 0,
    Size: Pagination.Size,
  },
  balanceAlertRules: [],
  balanceAlertRulesPagination: {
    Page: Pagination.Page,
    totalPages: 0,
    totalElements: 0,
    Size: Pagination.Size,
  },
};

const slice = createSlice({
  name: 'setting',
  initialState: settingInitialState,
  reducers: {
    setListSettingAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: SettingType[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.data = payload.data;
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setListCompanySecurityAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: SettingCompanySecurity[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.dataCompanySecurity = payload.data;
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setSettingFiltersAction(state, action: PayloadAction<SettingFilterState>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetSettingFiltersAction(state) {
      state.filters = {};
    },
    setDetailSettingAction(state, { payload }: PayloadAction<SettingDetailType>) {
      state.dataDetail = payload;
    },
    resetDetailSettingAction(state) {
      state.dataDetail = {};
    },
    setSettingLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setListAssignVirtualAccountAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: AssignVirtualAccountSetting[];
        pagination?: PaginationAPI;
        totalElements?: number;
        append?: boolean;
      }>,
    ) {
      if (payload.append) {
        state.dataAssignVA = [...state.dataAssignVA, ...payload.data];
      } else {
        state.dataAssignVA = payload.data;
      }
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setListAssignVirtualCardsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: AssignVirtualCardsSetting[];
        pagination?: PaginationAPI;
        totalElements?: number;
        append?: boolean;
      }>,
    ) {
      if (payload.append) {
        state.dataAssignVC = [...state.dataAssignVC, ...payload.data];
      } else {
        state.dataAssignVC = payload.data;
      }
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setListAssignGroupCardsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: AssignGroupCardsSetting[];
        pagination?: PaginationAPI;
        totalElements?: number;
        append?: boolean;
      }>,
    ) {
      if (payload.append) {
        state.dataAssignGC = [...state.dataAssignGC, ...payload.data];
      } else {
        state.dataAssignGC = payload.data;
      }
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setListTelegramBotsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: TelegramBot[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.telegramBots = payload.data;
      state.telegramBotsPagination = payload.pagination || state.telegramBotsPagination;
    },
    setListTransactionNotificationRulesAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: TransactionNotificationRule[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.transactionNotificationRules = payload.data;
      state.transactionNotificationRulesPagination =
        payload.pagination || state.transactionNotificationRulesPagination;
    },
    setListBalanceAlertRulesAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: BalanceAlertRule[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.balanceAlertRules = payload.data;
      state.balanceAlertRulesPagination = payload.pagination || state.balanceAlertRulesPagination;
    },
  },
});

export const {
  setListSettingAction,
  setSettingFiltersAction,
  resetSettingFiltersAction,
  setDetailSettingAction,
  resetDetailSettingAction,
  setListAssignVirtualAccountAction,
  setListAssignVirtualCardsAction,
  setListAssignGroupCardsAction,
  setListTelegramBotsAction,
  setListTransactionNotificationRulesAction,
  setListBalanceAlertRulesAction,
  setListCompanySecurityAction,
  setSettingLoading,
} = slice.actions;
export default slice.reducer;
